import {
  ParamsForMethod,
  TellescopeModelFromName,
  useAuth,
  useTellescopeInfiniteQuery,
  useTellescopeMutation,
  useTellescopeQuery,
  useUsersQuery,
} from "@tellescope/tanstack-query";
import { DashboardCard } from "@tellescope/ui";
import { useTranslation } from "react-i18next";
import { formatName, formatTime } from "@tellescope/utils";
import { useLocalTracks } from "@tellescope/video-calling";
import { ComponentProps, useCallback } from "react";
import { useVideoCallingStore } from "../../../store";
import { toast } from "sonner";

export interface EventsSectionProps {
  contact: TellescopeModelFromName<"endusers">;
}

export const EventsSection = ({ contact }: EventsSectionProps) => {
  const { t } = useTranslation("events");
  const { currentUser } = useAuth();
  const videoCalling = useVideoCallingStore();
  const { createLocalTracks } = useLocalTracks();
  const eventsQuery = useTellescopeInfiniteQuery({
    model: "calendar_events",
    method: "getSome",
    request: {
      filter: {
        attendees: {
          type: "enduser",
          id: contact.id,
        } as NonNullable<NonNullable<ParamsForMethod<"calendar_events", "getSome">>["filter"]>["attendees"], // TODO: fix sdk types. Using an array doesn't work here, but the sdk expects an array
      },
      limit: 500,
    },
    select: (data) => data.pages.flat(),
  });
  const { enduserIds, userIds } = eventsQuery.data?.reduce<{ enduserIds: string[]; userIds: string[] }>(
    (acc, curr) => {
      const newIds = curr.attendees.reduce<{ enduserIds: string[]; userIds: string[] }>(
        (acc, curr) => {
          if (curr.type === "enduser") {
            acc.enduserIds.push(curr.id);
          } else {
            acc.userIds.push(curr.id);
          }
          return acc;
        },
        { enduserIds: [], userIds: [] }
      );
      acc.enduserIds.push(...newIds.enduserIds);
      acc.userIds.push(...newIds.userIds);
      return acc;
    },
    { enduserIds: [], userIds: [] }
  ) ?? { enduserIds: [], userIds: [] };
  const usersQuery = useUsersQuery(userIds);

  const endusersQuery = useTellescopeQuery({
    model: "endusers",
    method: "getByIds",
    request: {
      ids: enduserIds,
    },
    enabled: !!enduserIds.length,
  });

  const updateEventMutation = useTellescopeMutation({
    model: "calendar_events",
    method: "updateOne",

    onError: () => {
      toast.error(t("Error updating calendar event. Please try again."));
    },
  });

  const onCancel = useCallback(
    (eventId: string) => {
      if (!currentUser) return;
      updateEventMutation.mutate({
        id: eventId,
        cancelledAt: new Date(),
        cancelReason: "Unknown", // TODO: get designs for cancellation reason as well as cancel action
        statusChangeSource: {
          identifier: currentUser.id,
          source: "user",
        },
      });
    },
    [currentUser, updateEventMutation]
  );

  const onNoShow = useCallback(
    (eventId: string) => {
      updateEventMutation.mutate({
        id: eventId,
        noShowedAt: new Date(),
      });
    },
    [updateEventMutation]
  );

  const onComplete = useCallback(
    (eventId: string) => {
      if (!currentUser) return;
      updateEventMutation.mutate({
        id: eventId,
        completedAt: new Date(),
        completedBy: currentUser.id,
      });
    },
    [currentUser, updateEventMutation]
  );

  const onVideoCall = useCallback(
    (event: NonNullable<typeof eventsQuery.data>[number]) => {
      if (!currentUser) return;

      // Check if current user is the host (creator of the event or has a host attendee entry)
      const isHost =
        event.creator === currentUser.id ||
        event.attendees.some((a) => a.type === "user" && a.id === currentUser.id && (a as { isHost?: boolean }).isHost);

      // Set up the meeting metadata and show the video call window
      videoCalling.setMeetingMeta({
        calendarEventId: event.id,
        eventTitle: event.title,
        isHost,
      });
      videoCalling.setIsWaitingRoom(true);
      videoCalling.setIsVisible(true);

      // Create local tracks (camera/mic) when entering waiting room
      createLocalTracks().catch(() => {
        // Error is handled and stored in state by createLocalTracks
      });
    },
    [currentUser, videoCalling, createLocalTracks]
  );

  if (eventsQuery.isLoading) {
    return (
      <div className="flex flex-1 items-center justify-center">
        <div className="text-center space-y-4">
          <div className="text-sm text-muted-foreground">{t("Loading events...")}</div>
        </div>
      </div>
    );
  }

  if (!eventsQuery.data?.length) {
    return (
      <div className="flex flex-1 items-center justify-center">
        <div className="text-center space-y-4">
          <div className="text-sm text-muted-foreground">{t("No events found")}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-y-auto space-y-4 pr-2">
      {eventsQuery.data?.map((event) => {
        const assignees = event.attendees.reduce<NonNullable<ComponentProps<typeof DashboardCard>["assignees"]>>(
          (acc, attendee) => {
            if (attendee.type === "user") {
              const user = usersQuery.data?.find((user) => user.id === attendee.id);
              if (user) {
                acc.push({
                  name: formatName(user),
                  src: user.url,
                });
              }
            } else {
              const enduser = endusersQuery.data?.matches.find((enduser) => enduser.id === attendee.id);
              if (enduser) {
                acc.push({
                  name: formatName(enduser),
                });
              }
            }
            return acc;
          },
          []
        );

        // Check if this event has Twilio video integration
        // Note: "Twilio" type will be added to SDK types in a future update
        const hasTwilioVideo = (event.videoIntegration as string) === "Twilio";

        return (
          <DashboardCard
            key={event.id}
            variant="event"
            title={event.title}
            eventDate={new Date(event.startTimeInMS)}
            eventTime={formatTime(event.startTimeInMS)}
            assignees={assignees}
            eventActions={{
              onComplete: () => onComplete(event.id),
              onCancel: () => onCancel(event.id),
              onNoShow: () => onNoShow(event.id),
              onVideoCall: hasTwilioVideo ? () => onVideoCall(event) : undefined,
            }}
            className="w-full"
          />
        );
      })}
    </div>
  );
};
